import { z } from 'zod';
import { idSchema, timestampsSchema } from './common';

/**
 * Typed improvement proposals (Epic M1). The first three are legacy/advisory;
 * the rest carry a structured `detail` payload an apply engine can act on.
 */
export const learningKinds = [
  'eval',
  'skill',
  'prompt',
  'prompt_update',
  'rule_change',
  'triage_adjustment',
  'kb_merge',
  'teach',
] as const;
export const learningKindSchema = z.enum(learningKinds);
export type LearningKind = z.infer<typeof learningKindSchema>;

/** Kinds the apply engine can transactionally enact on accept. */
export const appliableKinds = [
  'prompt_update',
  'rule_change',
  'triage_adjustment',
  'kb_merge',
  'teach',
] as const;

export const learningStatuses = ['proposed', 'accepted', 'rejected', 'applied'] as const;
export const learningStatusSchema = z.enum(learningStatuses);
export type LearningStatus = z.infer<typeof learningStatusSchema>;

export const learningSources = ['run', 'teach', 'insight'] as const;
export const learningSourceSchema = z.enum(learningSources);
export type LearningSource = z.infer<typeof learningSourceSchema>;

export const learningSuggestionSchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    workspaceId: idSchema.nullish(),
    runId: idSchema.nullish(),
    agentId: idSchema.nullish(),
    kind: learningKindSchema.default('eval'),
    source: learningSourceSchema.default('run'),
    title: z.string().default(''),
    detail: z.record(z.unknown()).default({}),
    status: learningStatusSchema.default('proposed'),
    decidedBy: idSchema.nullish(),
    reason: z.string().nullish(),
  })
  .merge(timestampsSchema);
export type LearningSuggestion = z.infer<typeof learningSuggestionSchema>;

export const learningDecisionSchema = z.object({
  accept: z.boolean(),
  reason: z.string().max(2000).optional(),
});
export type LearningDecision = z.infer<typeof learningDecisionSchema>;

// --- Typed `detail` payloads per appliable kind (Epic M1) ---

export const promptUpdateDetailSchema = z.object({
  agentId: idSchema,
  newPrompt: z.string().min(1),
  /** Optional eval set to gate the change (Epic M4). Falls back to any set for the agent. */
  evalSetId: idSchema.optional(),
});
export type PromptUpdateDetail = z.infer<typeof promptUpdateDetailSchema>;

export const ruleChangeDetailSchema = z.object({
  agentId: idSchema,
  /** Guardrail fields merged onto the agent's existing guardrails. */
  guardrails: z.record(z.unknown()),
});
export type RuleChangeDetail = z.infer<typeof ruleChangeDetailSchema>;

export const triageAdjustmentDetailSchema = z.object({
  workspaceId: idSchema.optional(),
  labelKey: z.string().min(1),
  /** Routing fields merged onto the taxonomy label's routing config. */
  routing: z.record(z.unknown()),
});
export type TriageAdjustmentDetail = z.infer<typeof triageAdjustmentDetailSchema>;

export const kbMergeDetailSchema = z.object({
  agentId: idSchema,
  knowledgeBaseId: idSchema,
});
export type KbMergeDetail = z.infer<typeof kbMergeDetailSchema>;

export const teachDetailSchema = z.object({
  agentId: idSchema,
  content: z.string().min(1),
});
export type TeachDetail = z.infer<typeof teachDetailSchema>;

/** Body for the teaching intake endpoint (Epic M2). */
export const teachRequestSchema = z.object({
  agentId: idSchema,
  workspaceId: idSchema.optional(),
  title: z.string().min(1).max(200),
  content: z.string().min(1),
});
export type TeachRequest = z.infer<typeof teachRequestSchema>;

/** Result of an apply attempt, including any eval gate outcome (Epic M4). */
export interface ApplyResult {
  id: string;
  status: LearningStatus;
  applied: boolean;
  target?: string;
  evalGate?: { ran: boolean; passed: boolean; passRate?: number; evalSetId?: string };
  reason?: string;
}

// --- Evolution insights (Epic M3) ---

export const evolutionInsightSchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    workspaceId: idSchema.nullish(),
    agentId: idSchema.nullish(),
    metricKey: z.string(),
    value: z.number(),
    window: z.string().default('all'),
    detail: z.record(z.unknown()).default({}),
  })
  .merge(timestampsSchema);
export type EvolutionInsight = z.infer<typeof evolutionInsightSchema>;
